import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import HelpCenter from '../HelpCenter';
import SinoAvisos from './SinoAvisos';
import BotaoAgenda from './BotaoAgenda';
import DashboardOperacao from './DashboardOperacao';
import PainelDesempenho from './PainelDesempenho';
import { abasDoUsuario, rotaInicialDoUsuario, ABA_OPERACAO, ABA_DESEMPENHO, ABA_RH, ABA_ESCRITORIO } from './abas-home';
// ⚠️ A Home precisa das capacidades para saber para onde mandar quem chega sem
//    nenhuma aba: o destino é o primeiro módulo do MENU dela, e o menu passou a
//    depender delas em 18/09/2026.
import { useCapacidades } from '../../utils/capacidades';
import { COR, FONTE, SOMBRA, RAIO, cartao, TituloPainel, MultiSelectLojas } from './ui';
import {
    Search, LogOut, Lock, ClipboardList, Users, BarChart3, Menu,
    Palmtree, GraduationCap, Workflow, ClipboardCheck, Star, HelpCircle,
    CheckCircle, Briefcase, Wallet, Calculator, ChevronRight,
    Headset, Wrench, Truck, ShoppingCart,
    LayoutDashboard, Building2, Gauge,
} from 'lucide-react';

// =====================================================================
// HOME (saguão pós-login)
// =====================================================================
// Reconstrução FIEL do desenho do Marketing (Figma — frames "Home" e
// "Home - Bloqueado"). O layout inteiro é montado aqui; os cards que ainda
// NÃO têm funcionalidade aparecem em estado BLOQUEADO (cadeado + "Em breve"),
// exatamente como no frame "Home - Bloqueado". Conforme o backend de cada
// bloco for entrando, é só remover o <Bloqueio> em volta dele.
//
// A tela está LIBERADA para todos os usuários (sem gate de teste).
// O que JÁ é funcional (lê de /api/home/*):
//   - Indicadores da Loja  (contadores reais + 2 quadros "a definir")
//   - Aniversariantes do Mês
//   - Atividades pendentes (itens conforme o perfil)
//   - Agenda Corporativa (Teams/M365)
// O resto (Acesso Rápido, Comunicados, Destaques, busca global, "Ver
// comunicados") está como CASCA bloqueada (cadeado + "Em breve").
//
// Estilos inline (convenção do projeto — não há Tailwind). Fonte Raleway
// (mesma do Figma; carregada no public/index.html). Layout elástico em grid
// pra acompanhar a sidebar aberta/recolhida e telas menores.

// Paleta e tokens visuais (COR, FONTE, SOMBRA, RAIO, cartao) agora vivem em
// ./ui.js — compartilhados com o Dashboard de Operação e futuros blocos da Home.

// Quem alcança cada aba (Operação · RH · Escritório) e em qual delas a Home abre
// moram em ./abas-home.js — é regra de ACESSO, tem dono e tem teste. Aqui ficou
// só o desenho da barra.

// Altura dos três cards da aba Escritório (Atividades pendentes · Agenda
// Corporativa · Destaques do Mês). ÚNICA de propósito: é ela — e não o
// `align-items` — que mantém os três terminando na mesma linha, mesmo com
// conteúdos de tamanhos diferentes.
//
// ⚠️ 259 → 520px em 11/09/2026 (Julia), junto com a saída dos cards Comunicados,
//    Indicadores da Loja e Aniversariantes do Mês. Os três de baixo passaram a
//    ser a única linha da aba, e nos 259px antigos a página terminava numa faixa
//    estreita no topo com o resto em branco. Mudar aqui mexe nos TRÊS.
const ALTURA_CARD = '520px';

// Visual do botão do filtro de loja — o mesmo dos selects do dashboard, para os
// dois filtros da Home não parecerem de telas diferentes.
const ESTILO_SELECT = {
    fontFamily: FONTE, fontSize: '13px', fontWeight: 600, color: COR.texto,
    border: `1px solid ${COR.bordaSuave}`, borderRadius: '8px', padding: '7px 12px',
    backgroundColor: '#fff', cursor: 'pointer', outline: 'none',
};

// --- Rótulo e ícone de cada aba ---------------------------------------------
// O ícone é o mesmo que o antigo toggle do DashboardOperacao usava, para quem já
// usava o dashboard não precisar reaprender a barra.
const ROTULO_ABA = {
    [ABA_OPERACAO]: { texto: 'Operação', icone: LayoutDashboard },
    [ABA_DESEMPENHO]: { texto: 'Desempenho', icone: Gauge },
    [ABA_RH]: { texto: 'RH', icone: Users },
    [ABA_ESCRITORIO]: { texto: 'Escritório', icone: Building2 },
};

// --- Barra de abas da Home ---------------------------------------------------
// ⚠️ Com UMA aba só, a barra não é desenhada: ela só existe para trocar de painel,
//    e quem tem uma aba não tem para onde trocar. O que sobra é o título dizendo
//    que painel é aquele — papel que no dashboard antigo cabia ao selo "Visão RH".
function BarraAbas({ abas, ativa, onTrocar, compacta = false }) {
    if (!abas || abas.length <= 1) {
        const so = abas && abas.length === 1 ? ROTULO_ABA[abas[0]] : null;
        if (!so) return null;
        const Icone = so.icone;
        return (
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px', marginTop: '18px',
                backgroundColor: COR.vermelhoEscuro, color: '#fff', fontFamily: FONTE,
                fontSize: '14px', fontWeight: 700, padding: '9px 18px', borderRadius: RAIO, boxShadow: SOMBRA,
            }}>
                <Icone size={16} />{so.texto}
            </div>
        );
    }
    return (
        <div style={{
            display: 'inline-flex', gap: '8px', marginTop: '18px',
            backgroundColor: '#efecec', padding: '5px', borderRadius: '12px',
            maxWidth: '100%', overflowX: 'auto',
        }}>
            {abas.map((a) => {
                const { texto, icone: Icone } = ROTULO_ABA[a];
                const ativo = a === ativa;
                return (
                    <button key={a} type="button" onClick={() => onTrocar(a)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '7px', border: 'none', cursor: 'pointer',
                        fontFamily: FONTE, fontSize: compacta ? '13px' : '14px', fontWeight: 700,
                        padding: compacta ? '8px 14px' : '9px 18px', borderRadius: RAIO, whiteSpace: 'nowrap',
                        backgroundColor: ativo ? COR.vermelhoEscuro : '#fff', color: ativo ? '#fff' : '#777',
                        boxShadow: ativo ? SOMBRA : 'none',
                    }}>
                        <Icone size={16} />{texto}
                    </button>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------
// Saudação conforme a hora local + data por extenso em pt-BR
// ---------------------------------------------------------------------
function saudacaoPorHora() {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
}
function dataPorExtenso() {
    const txt = new Date().toLocaleDateString('pt-BR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    return txt.charAt(0).toUpperCase() + txt.slice(1);
}

// Nome para o cabeçalho: só o PRIMEIRO + ÚLTIMO nome, com a inicial maiúscula e o
// resto minúsculo (ex.: "MARIA DA SILVA SANTOS" → "Maria Santos"). Evita nome
// comprido e o caixa-alta que vem do cadastro.
function nomeCabecalho(nomeCompleto) {
    if (!nomeCompleto) return '';
    const partes = nomeCompleto.trim().split(/\s+/);
    const cap = (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    if (partes.length === 1) return cap(partes[0]);
    return `${cap(partes[0])} ${cap(partes[partes.length - 1])}`;
}

function Home() {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);   // alterna o layout (desktop ↔ mobile)
    const [user, setUser] = useState(null);
    // As capacidades desta pessoa — é delas que sai o menu dos módulos migrados,
    // e por isso o destino de quem chega aqui sem nenhuma aba.
    const { caps } = useCapacidades();
    const [pendencias, setPendencias] = useState(null);      // { estruturado, itens: [{titulo, quantidade, rota}] }
    const [ajudaAberta, setAjudaAberta] = useState(false);   // modal do Manual da Tela

    // Recolhe a barra lateral automaticamente em telas pequenas + alterna o layout mobile
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ---------------------------------------------------------------
    // Aba ativa da Home (Operação · RH · Escritório)
    // ---------------------------------------------------------------
    // `abaEscolhida` guarda só o CLIQUE da pessoa; a aba de fato é derivada.
    // Dois motivos para não guardar a aba pronta no estado:
    //   1. o `user` chega DEPOIS da 1ª renderização (vem do localStorage num
    //      efeito), então no primeiro quadro ainda não há como saber a aba inicial;
    //   2. derivando, a escolha é VALIDADA a cada render contra as abas que o
    //      usuário alcança — uma aba que ele não tem nunca fica na tela.
    // `null` = ainda não clicou em nada → vale a primeira aba da lista, que é a
    // regra de aba inicial (ver o comentário de `abaInicial` em abas-home.js).
    const [abaEscolhida, setAbaEscolhida] = useState(null);
    const abas = useMemo(() => abasDoUsuario(user), [user]);
    const aba = abas.includes(abaEscolhida) ? abaEscolhida : (abas[0] || null);

    // ⚠️⚠️ SEM NENHUMA ABA → não fica na Home. Enquanto a aba Escritório está
    //    fechada ao ID 57 (ver abas-home.js), a maioria dos perfis de escritório
    //    cai aqui. Sem este desvio a pessoa veria uma tela EM BRANCO — sem erro e
    //    sem pista de que o resto do portal continua inteiro.
    // ⚠️ `replace: true` para o botão Voltar do navegador não trazer a pessoa de
    //    volta à Home vazia, num vai-e-volta sem fim.
    // ⚠️ Espera o `user` chegar (ele vem do localStorage num efeito): no primeiro
    //    quadro `abas` é [] para TODO mundo, e redirecionar ali expulsaria até
    //    quem tem as três abas.
    useEffect(() => {
        if (!user || abas.length) return;
        const destino = rotaInicialDoUsuario(user, caps);
        // Sem destino = não alcança módulo nenhum. Não redireciona: não há para
        // onde, e mandar para lugar nenhum daria laço. A tela mostra o aviso.
        if (destino) navigate(destino, { replace: true });
    }, [user, abas, navigate, caps]);

    // Usuário logado (mesma fonte da Sidebar)
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try { setUser(JSON.parse(userStr)); } catch (e) { /* ignora */ }
        }
    }, []);

    // Filtro de LOJA da aba Escritório (vazio = rede toda). ⚠️ Só LOJA: filtro de
    // DATA foi descartado em 11/09/2026 porque pendência é dívida em aberto
    // AGORA, não fato datado — o RTB12 é sempre o mês anterior, o Atos sempre o
    // corrente, o Petty Cash sempre os meses encerrados. O período é a DEFINIÇÃO
    // de cada uma, não um recorte aplicável por fora.
    const [lojasPend, setLojasPend] = useState([]);

    // Pendências — carga única ao montar.
    // ⚠️ Buscava também `/api/home/aniversariantes` e `/api/home/indicadores`; as
    //    duas saíram em 11/09/2026 com os cards que as consumiam. As ROTAS seguem
    //    de pé (a tela /aniversariantes usa a primeira) — o que saiu foi a chamada
    //    que a Home fazia a cada abertura para alimentar card que não existe mais.
    // ⚠️ Recarrega ao mexer no filtro de loja — quem recorta é o BACKEND, não a
    //    tela. Filtrar no front daria número errado: as quantidades chegam já
    //    agregadas (um COUNT por card), não como lista de linhas para peneirar.
    // ⚠️ `vivo` descarta a resposta de um filtro abandonado: clicar em 3 lojas
    //    rápido dispara 3 rodadas, e sem isso a 1ª a voltar poderia sobrescrever.
    useEffect(() => {
        let vivo = true;
        (async () => {
            const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
            const qs = lojasPend.length ? `?lojas=${encodeURIComponent(lojasPend.join(','))}` : '';
            try {
                const r = await fetch(`/api/home/pendencias${qs}`, { headers });
                if (!vivo) return;
                if (r.ok) setPendencias(await r.json());
            } catch (e) { console.error('[Home] pendencias:', e); }
        })();
        return () => { vivo = false; };
    }, [lojasPend]);

    // Logout — mesma lógica que estava na Sidebar (agora vive na barra superior)
    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));
    const primeiroNome = user && user.nome ? user.nome.split(' ')[0] : '';
    const inicial = primeiroNome ? primeiroNome.charAt(0).toUpperCase() : '?';

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COR.paginaFundo, fontFamily: FONTE }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />

            {isMobile ? (
                <HomeMobile
                    user={user}
                    abas={abas}
                    aba={aba}
                    onTrocarAba={setAbaEscolhida}
                    pendencias={pendencias}
                    navigate={navigate}
                    onLogout={handleLogout}
                    onAbrirMenu={() => setIsCollapsed(false)}
                />
            ) : (
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

                {/* ============================================================ */}
                {/* BARRA SUPERIOR: busca (casca) · sino (casca) · usuário        */}
                {/* ============================================================ */}
                <div style={{
                    height: '85px', backgroundColor: COR.barraFundo, boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'center', gap: '20px', padding: '0 30px',
                    position: 'sticky', top: 0, zIndex: 30,
                }}>
                    {/* Busca global — CASCA: visual pronto, sem função ainda */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: COR.buscaFundo,
                        borderRadius: RAIO, padding: '0 13px', height: '55px', width: '402px', maxWidth: '40%',
                    }} title="Em breve">
                        <input
                            disabled
                            placeholder="Buscar no sistema"
                            style={{
                                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                                fontFamily: FONTE, fontWeight: 300, fontSize: '13px', color: '#555', cursor: 'not-allowed',
                            }}
                        />
                        <Search size={20} color="#7a7a7a" />
                    </div>

                    <div style={{ flex: 1 }} />

                    {/* Sino de avisos (badge + lista + pop-up do aviso) */}
                    <SinoAvisos cor={COR.amarelo} tamanhoIcone={24} larguraLista={300} topoLista={46} />

                    {/* Agenda Corporativa num modal. Aqui na barra ela alcança TODOS os
                        perfis — inclusive os de gestão, que caem no Dashboard de Operação
                        e por isso não enxergam o card da agenda lá embaixo. */}
                    <BotaoAgenda cor={COR.amarelo} tamanhoIcone={24} />

                    {/* Manual da Tela — só ícone; abre o Manual (HelpCenter) */}
                    <button
                        onClick={() => setAjudaAberta(true)}
                        title="Manual da Tela"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, display: 'flex', alignItems: 'center', padding: '6px' }}
                    >
                        <HelpCircle size={24} />
                    </button>

                    {/* Divisória */}
                    <div style={{ width: '1px', height: '40px', backgroundColor: '#d8d8d8' }} />

                    {/* Avatar + nome + perfil */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: COR.vermelhoEscuro, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '16px', flexShrink: 0,
                        }}>{inicial}</div>
                        <div style={{ lineHeight: 1.25 }}>
                            <div style={{ fontWeight: 600, color: COR.texto, fontSize: '12px' }}>{user ? nomeCabecalho(user.nome) : ''}</div>
                            <div style={{ fontWeight: 300, color: '#555', fontSize: '10px' }}>{user ? user.perfil : ''}</div>
                        </div>
                    </div>

                    {/* Sair — substituiu a engrenagem; logout veio da Sidebar */}
                    <button
                        onClick={handleLogout}
                        title="Sair"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, display: 'flex', alignItems: 'center', padding: '6px' }}
                    >
                        <LogOut size={22} />
                    </button>
                </div>

                {/* ============================================================ */}
                {/* CONTEÚDO                                                      */}
                {/* ============================================================ */}
                <div style={{ flex: 1, padding: '28px 32px 16px' }}>

                    {/* Saudação + botão "Ver comunicados" (bloqueado) */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 500, color: COR.texto }}>
                                {saudacaoPorHora()}{primeiroNome ? `, ${primeiroNome}` : ''}! 👋
                            </h1>
                            <p style={{ margin: '6px 0 0', color: COR.texto, fontSize: '11px', fontWeight: 400 }}>{dataPorExtenso()}</p>
                        </div>

                        {/* Botão "Ver comunicados" — bloqueado (cadeado), mas com as cores do
                            Figma: fundo vermelho escuro com megafone e texto brancos. 
                        <div style={{ ...cartao, width: '254px', height: '63px', backgroundColor: COR.vermelhoEscuro }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', height: '100%' }}>
                                <Megaphone size={22} color={COR.branco} />
                                <span style={{ fontWeight: 600, fontSize: '16px', color: COR.branco }}>Ver comunicados</span>
                            </div>
                            <CadeadoCanto />
                        </div>*/}
                    </div>

                    {/* Barra de abas: UMA Home para todos os perfis desde 11/09/2026.
                        Antes daqui saíam DUAS telas diferentes conforme o perfil — o
                        dashboard (com a barra Operação|RH por dentro) ou a home
                        genérica. Agora a barra é da PÁGINA e a home genérica virou a
                        aba Escritório. Quem alcança o quê: ./abas-home.js. */}
                    <BarraAbas abas={abas} ativa={aba} onTrocar={setAbaEscolhida} />

                    <div style={{ marginTop: '18px' }}>
                    {(aba === ABA_OPERACAO || aba === ABA_RH) && <DashboardOperacao visao={aba} />}
                    {aba === ABA_ESCRITORIO && (
                    <>
                    {/* ---------- ACESSO RÁPIDO (6 atalhos, todos bloqueados) ---------- */}
                    <h2 style={{ margin: '26px 0 14px', fontSize: '16px', fontWeight: 600, color: COR.texto }}>Acesso Rápido</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '18px' }}>
                        {/* Atalhos alternam vermelho/amarelo (padrão do Figma): ímpares vermelho, pares amarelo. */}
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                        {/* Colaboradores e Indicadores entraram no "Em breve" em 11/09/2026
                            (Julia). As telas continuam existindo e alcançáveis pelo menu —
                            o que saiu foi o ATALHO daqui, não o acesso. */}
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                        <Bloqueio><CardAtalho semIcone /></Bloqueio>
                    </div>

                    {/* ---------- Atividades | Agenda | Destaques ----------
                        Em 11/09/2026 (Julia) esta deixou de ser a linha "de baixo" e
                        passou a ser a ÚNICA: saíram os cards Comunicados, Indicadores da
                        Loja e Aniversariantes do Mês, que formavam a linha do meio.
                        ⚠️ Os três cresceram de 259 para ALTURA_CARD justamente para
                        ocupar o espaço liberado — sem isso a aba terminaria numa faixa
                        estreita no topo, com a página vazia embaixo. A altura é ÚNICA
                        para os três: é ela que os mantém terminando na mesma linha. */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '18px', marginTop: '20px' }}>
                        {/* Atividades pendentes — FUNCIONAL (itens conforme o perfil) */}
                        <div style={{ ...cartao, minHeight: ALTURA_CARD, height: '100%', boxSizing: 'border-box', padding: '18px', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                <TituloPainel icone={<ClipboardCheck size={22} color={COR.amarelo} />} texto="Atividades pendentes" />
                                <MultiSelectLojas lojas={pendencias?.lojas || []} selecionados={lojasPend} onChange={setLojasPend} selectStyle={ESTILO_SELECT} />
                            </div>
                            {/* ⚠️ O filtro NÃO alcança tudo, e a nota existe para isso não
                                virar dúvida: triagem de currículos, RTB12 e Atos são
                                processos CENTRAIS, sem loja (o backend os marca com
                                `escopo: 'rede'`). Eles continuam aparecendo mesmo com uma
                                loja escolhida — sumir leria como "não há pendência". */}
                            {lojasPend.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>
                                    Processos centrais (triagem, RTB12, Atos) não se recortam por loja e seguem visíveis.
                                </div>
                            )}
                            {/* Rola por dentro quando há muita pendência (ex.: Admin), mantendo
                                a altura alinhada com Agenda/Destaques. ⚠️ Sem `maxHeight` fixo:
                                o `flex:1` + `minHeight:0` deixa a lista usar TODA a altura do
                                card — que agora é bem maior — e só então rolar. */}
                            <div style={{ flex: 1, minHeight: 0, marginTop: '14px', overflowY: 'auto' }}>
                                <PainelPendencias pendencias={pendencias} navigate={navigate} />
                            </div>
                        </div>

                        {/* ⚠️ A Agenda Corporativa SAIU daqui em 11/09/2026 (Julia): ela já
                            está no ÍCONE DE CALENDÁRIO da barra do topo, que alcança todos
                            os perfis — inclusive os que não têm esta aba. Ter as duas era
                            a mesma agenda em dois lugares. O componente segue vivo e é o
                            mesmo que o modal do cabeçalho monta. */}

                        <Bloqueio>
                            <div style={{ ...cartao, minHeight: ALTURA_CARD, height: '100%', boxSizing: 'border-box', padding: '18px', display: 'flex', flexDirection: 'column' }}>
                                <TituloPainel icone={<Star size={22} color={COR.amarelo} />} texto="Destaques do Mês" />
                                <div style={{ marginTop: '16px', backgroundColor: COR.creme, borderRadius: RAIO, flex: 1 }} />
                            </div>
                        </Bloqueio>
                    </div>
                    </>
                    )}
                    {aba === ABA_DESEMPENHO && <PainelDesempenho />}
                    </div>

                    {/* ---------- RODAPÉ ---------- */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginTop: '28px', color: COR.rodape, fontFamily: "'Poppins', sans-serif", fontWeight: 300, fontSize: '12px' }}>
                        <span>2026 Grupo Correia. Todos os direitos reservados</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <span>Política de Privacidade</span>
                            <span style={{ color: '#d0d0d0' }}>|</span>
                            <span>Termos de Uso</span>
                            <span style={{ color: '#d0d0d0' }}>|</span>
                            <span>Suporte</span>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Modal do Manual da Tela (sem `modulo` → manual geral do portal) */}
            {/* Manual da Tela — verbetes da própria Home (HelpCenter › manualHome):
                saguão, dashboard Operação/RH, calendário/agenda e sino de avisos.
                Antes daqui saía o manual do portal INTEIRO (o "junta tudo"), que
                despejava os verbetes de todos os módulos em quem só queria entender
                a tela em que está. */}
            <HelpCenter
                isOpen={ajudaAberta}
                onClose={() => setAjudaAberta(false)}
                modulo="home"
                secaoInicial="home-visao-geral"
            />
        </div>
    );
}

// =====================================================================
// HOME — versão MOBILE (≤768px), conforme o frame "Versão Mobile" do Figma.
// Topo (logo + hambúrguer | sino + nome/perfil + sair), saudação centralizada,
// busca em largura cheia, Acesso Rápido em carrossel horizontal, cards
// EMPILHADOS reaproveitando os dados reais do desktop (Indicadores, Aniversariantes,
// Atividades), e rodapé vermelho. O hambúrguer abre o Sidebar (overlay mobile).
// =====================================================================
function HomeMobile({ user, abas, aba, onTrocarAba, pendencias, navigate, onLogout, onAbrirMenu }) {
    const primeiroNome = user && user.nome ? user.nome.split(' ')[0] : '';

    return (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* ---------- TOPO ---------- */}
            <div style={{ height: '64px', backgroundColor: COR.barraFundo, boxShadow: '0 2px 4px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', position: 'sticky', top: 0, zIndex: 30 }}>
                {/* Esquerda: logo (arco) + hambúrguer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src="/logotipo.png" alt="Grupo Correia" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
                    <button onClick={onAbrirMenu} title="Menu" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', padding: '4px' }}>
                        <Menu size={26} />
                    </button>
                </div>
                {/* Direita: sino + agenda + divisória + nome/perfil + sair */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <SinoAvisos cor={COR.amarelo} tamanhoIcone={22} larguraLista={260} topoLista={40} />
                    <BotaoAgenda cor={COR.amarelo} tamanhoIcone={22} />
                    <div style={{ width: '1px', height: '34px', backgroundColor: '#d8d8d8' }} />
                    <div style={{ lineHeight: 1.2, textAlign: 'right', maxWidth: '130px' }}>
                        <div style={{ fontWeight: 600, color: COR.texto, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user ? nomeCabecalho(user.nome) : ''}</div>
                        <div style={{ fontWeight: 300, color: '#555', fontSize: '10px' }}>{user ? user.perfil : ''}</div>
                    </div>
                    <button onClick={onLogout} title="Sair" style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, display: 'flex', alignItems: 'center', padding: '4px' }}>
                        <LogOut size={20} />
                    </button>
                </div>
            </div>

            {/* ---------- CONTEÚDO ---------- */}
            <div style={{ flex: 1, padding: '20px 16px 0' }}>
                {/* Saudação centralizada */}
                <h1 style={{ margin: 0, textAlign: 'center', fontSize: '30px', fontWeight: 500, color: COR.texto }}>
                    {saudacaoPorHora()}{primeiroNome ? `, ${primeiroNome}` : ''}!
                </h1>
                <p style={{ margin: '6px 0 0', textAlign: 'center', color: COR.texto, fontSize: '14px' }}>{dataPorExtenso()}</p>

                {/* Busca (casca) — largura cheia */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: COR.buscaFundo, borderRadius: RAIO, padding: '0 14px', height: '48px', marginTop: '18px' }} title="Em breve">
                    <input disabled placeholder="Buscar no site" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONTE, fontWeight: 300, fontSize: '14px', color: '#555', cursor: 'not-allowed' }} />
                    <Search size={20} color="#7a7a7a" />
                </div>

                {/* Mesma barra de abas do desktop, em versão compacta — ela rola
                    na horizontal quando as três não cabem na largura do celular. */}
                <BarraAbas abas={abas} ativa={aba} onTrocar={onTrocarAba} compacta />

                <div style={{ marginTop: '18px' }}>
                {(aba === ABA_OPERACAO || aba === ABA_RH) && <DashboardOperacao visao={aba} />}
                {aba === ABA_ESCRITORIO && (
                <>
                {/* Acesso Rápido — carrossel horizontal */}
                        <h2 style={{ textAlign: 'center', margin: '22px 0 14px', fontSize: '16px', fontWeight: 600, color: COR.texto }}>Acesso Rápido</h2>
                        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px', WebkitOverflowScrolling: 'touch' }}>
                            <AtalhoMobile icone={<ClipboardList size={26} />} rotulo="Solicitações" bloqueado />
                            {/* Bloqueados em 11/09/2026 (Julia), como no desktop: as telas
                                continuam no menu, o que saiu foi o atalho daqui. */}
                            <AtalhoMobile icone={<Users size={26} />} rotulo="Colaboradores" bloqueado cor={COR.amarelo} corFundo={COR.amareloFundo} />
                            <AtalhoMobile icone={<BarChart3 size={26} />} rotulo="Indicadores" bloqueado />
                            <AtalhoMobile icone={<Palmtree size={26} />} rotulo="Férias" bloqueado cor={COR.amarelo} corFundo={COR.amareloFundo} />
                            <AtalhoMobile icone={<GraduationCap size={26} />} rotulo="Treinamentos" bloqueado />
                            <AtalhoMobile icone={<Workflow size={26} />} rotulo="Processos" bloqueado cor={COR.amarelo} corFundo={COR.amareloFundo} />
                        </div>

                        {/* Cards empilhados (funcionais, reaproveitando os dados do desktop) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '18px' }}>
                            {/* Atividades pendentes — funcional */}
                            <div style={{ ...cartao, padding: '18px', display: 'flex', flexDirection: 'column', minHeight: '260px' }}>
                                <TituloPainel icone={<ClipboardCheck size={22} color={COR.amarelo} />} texto="Atividades pendentes" />
                                <div style={{ marginTop: '14px' }}>
                                    <PainelPendencias pendencias={pendencias} navigate={navigate} />
                                </div>
                            </div>

                            {/* Destaques — bloqueado */}
                            <Bloqueio>
                                <div style={{ ...cartao, padding: '18px', display: 'flex', flexDirection: 'column', minHeight: '260px' }}>
                                    <TituloPainel icone={<Star size={22} color={COR.amarelo} />} texto="Destaques do Mês" />
                                    <div style={{ marginTop: '16px', backgroundColor: COR.creme, borderRadius: RAIO, flex: 1, minHeight: '70px' }} />
                                </div>
                            </Bloqueio>
                        </div>
                    </>
                )}
                {aba === ABA_DESEMPENHO && <PainelDesempenho />}
                </div>
            </div>

            {/* ---------- RODAPÉ (vermelho, full-bleed) ---------- */}
            <div style={{ marginTop: '24px', backgroundColor: COR.vermelhoEscuro, color: '#fff', padding: '16px', textAlign: 'center', fontFamily: "'Poppins', sans-serif", fontWeight: 300, fontSize: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <span>Política de Privacidade</span>
                    <span style={{ opacity: 0.6 }}>|</span>
                    <span>Termos de Uso</span>
                    <span style={{ opacity: 0.6 }}>|</span>
                    <span>Suporte</span>
                </div>
                <div style={{ opacity: 0.9 }}>2026 Grupo Correia. Todos os direitos reservados</div>
            </div>
        </div>
    );
}

// Atalho compacto do Acesso Rápido (mobile): círculo com ícone + rótulo.
// `onClick` → clicável; `bloqueado` → esmaecido + cadeado no canto, sem ação.
function AtalhoMobile({ icone, rotulo, onClick, bloqueado, cor = COR.vermelhoEscuro, corFundo = COR.iconeFundo }) {
    return (
        <div onClick={bloqueado ? undefined : onClick} style={{ ...cartao, position: 'relative', flex: '0 0 auto', width: '92px', minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 6px', textAlign: 'center', cursor: (!bloqueado && onClick) ? 'pointer' : 'default', opacity: bloqueado ? 0.55 : 1 }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: corFundo, color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icone}</div>
            <div style={{ fontWeight: 600, fontSize: '12px', color: COR.texto, lineHeight: 1.1 }}>{rotulo}</div>
            {bloqueado && (
                <div style={{ position: 'absolute', top: '6px', right: '6px', color: '#9a9a9a' }} title="Em breve"><Lock size={12} /></div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Componentes auxiliares
// ---------------------------------------------------------------------

// Card de Acesso Rápido: elipse clara + ícone + título + subtítulo.
// Com `onClick` vira clicável (cursor de mão); sem ele, é só visual (cards bloqueados).
// `cor`/`corFundo` permitem variar a cor do ícone/elipse por card (como no Figma,
// onde cada atalho tem sua cor); o padrão é o vermelho do portal.
// `semIcone` esconde a elipse do ícone: usado nos cards bloqueados, onde a
// sobreposição "Em breve" (cadeado central) cairia em cima do ícone e o texto
// ficaria sobressaindo do ícone que aparece atrás.
function CardAtalho({ icone, titulo, sub, onClick, cor = COR.vermelhoEscuro, corFundo = COR.iconeFundo, semIcone }) {
    return (
        <div onClick={onClick} style={{ ...cartao, minHeight: '168px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '18px', textAlign: 'center', cursor: onClick ? 'pointer' : 'default' }}>
            {!semIcone && (
                <div style={{ width: '70px', height: '70px', borderRadius: '50%', backgroundColor: corFundo, color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
                    {icone}
                </div>
            )}
            {titulo && <div style={{ fontWeight: 600, fontSize: '16px', color: COR.texto }}>{titulo}</div>}
            {sub && <div style={{ fontWeight: 600, fontSize: '12px', color: COR.subtitulo }}>{sub}</div>}
        </div>
    );
}

// ⚠️ `ColunaIndicador` e `ChipVariacao` (as colunas ▲▼ do card "Indicadores da
//    Loja") foram REMOVIDOS em 11/09/2026, com o card que os usava. Estão no
//    histórico do git — se o card voltar, é de lá que saem, não reescritos.

// O TituloPainel (cabeçalho ícone + texto dos cards) morava aqui; migrou para o
// ./ui.js quando a agenda passou a ser montada também fora da Home (modal do
// cabeçalho), que não tem como recebê-lo por prop daqui.

// Estado vazio padrão
function Vazio({ texto }) {
    return <div style={{ color: '#999', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>{texto}</div>;
}

// Conteúdo do card "Atividades pendentes": lista os itens do perfil que têm
// pendência (quantidade > 0), cada um clicável para a tela responsável.
// Estados: carregando · perfil sem regra estruturada · tudo em dia · lista.
// =====================================================================
// Atividades pendentes — agrupadas por SEÇÃO (o módulo dono da pendência)
// =====================================================================
// Antes de 11/09/2026 era uma lista PLANA de até 6 itens. Com 17 cards, plana
// vira parede: o Admin recebe tudo de todos os módulos sem hierarquia nenhuma.
// A seção vem do campo `modulo` que o backend carimba em cada item.
//
// ⚠️ SEÇÃO SÓ APARECE SE TIVER PENDÊNCIA (mantém o comportamento anterior: item
//    com quantidade 0 nunca foi mostrado). Com NADA em aberto, segue o
//    "Tudo em dia! 🎉" — a tela não vira uma lista de zeros.
//
// ⚠️ A seção REDE existe porque nem toda pendência tem loja: triagem de
//    currículos, RTB12 e Atos são processos CENTRAIS. O backend as marca com
//    `escopo: 'rede'` e elas ficam fora do filtro de loja — escondê-las ao
//    escolher "só ANV" leria como "não há pendência", quando o certo é "esta não
//    se recorta por loja".

// Nome, cor e ícone de cada seção. ⚠️ As cores são as da IDENTIDADE de cada
// módulo (docs/ARQUITETURA.md › Paleta dos módulos) — a mesma que o `cor=` do
// ModuloHeader usa no hub. Não inventar tom aqui: a pessoa reconhece o módulo
// pela cor antes de ler o título.
const SECOES = {
    chamados: { titulo: 'Chamados', cor: '#6b21a8', icone: Headset },
    manutencao: { titulo: 'Manutenção', cor: '#4f46e5', icone: Wrench },
    logistica: { titulo: 'Logística', cor: '#0e7490', icone: Truck },
    compras: { titulo: 'Compras', cor: '#e3a81d', icone: ShoppingCart },
    operacional: { titulo: 'Operacional', cor: '#475569', icone: ClipboardCheck },
    rh: { titulo: 'RH', cor: '#d81d1d', icone: Users },
    financeiro: { titulo: 'Financeiro', cor: '#0d7a74', icone: Wallet },
    contabilidade: { titulo: 'Contábil', cor: '#16a34a', icone: Calculator },
    processoseletivo: { titulo: 'Recrutamento', cor: '#e11d48', icone: Briefcase },
};
// Ordem das seções na tela. Quem não estiver aqui cai no fim, em ordem alfabética
// — assim um módulo novo aparece mesmo que alguém esqueça de listá-lo.
const ORDEM_SECOES = ['chamados', 'manutencao', 'logistica', 'compras', 'operacional', 'rh', 'financeiro', 'contabilidade', 'processoseletivo'];
const SECAO_PADRAO = { titulo: 'Outras', cor: COR.vermelhoEscuro, icone: ClipboardCheck };

function PainelPendencias({ pendencias, navigate }) {
    if (!pendencias) return <Vazio texto="Carregando…" />;

    if (!pendencias.estruturado) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#999', textAlign: 'center', padding: '12px' }}>
                <Lock size={22} />
                <span style={{ fontSize: '13px' }}>As pendências deste perfil ainda não foram estruturadas.</span>
            </div>
        );
    }

    const pendentes = (pendencias.itens || []).filter((i) => i.quantidade > 0);
    if (pendentes.length === 0) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#16a34a', textAlign: 'center' }}>
                <CheckCircle size={26} />
                <span style={{ fontSize: '14px', fontWeight: 600 }}>Tudo em dia! 🎉</span>
            </div>
        );
    }

    // Agrupa mantendo a ordem de ORDEM_SECOES; módulo desconhecido vai para o fim.
    const grupos = new Map();
    for (const item of pendentes) {
        const chave = item.modulo || 'outras';
        if (!grupos.has(chave)) grupos.set(chave, []);
        grupos.get(chave).push(item);
    }
    const chaves = [...grupos.keys()].sort((a, b) => {
        const ia = ORDEM_SECOES.indexOf(a), ib = ORDEM_SECOES.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {chaves.map((chave) => {
                const secao = SECOES[chave] || SECAO_PADRAO;
                const itens = grupos.get(chave);
                const total = itens.reduce((acc, i) => acc + i.quantidade, 0);
                const IconeSecao = secao.icone;
                return (
                    <div key={chave}>
                        {/* Cabeçalho da seção: ícone + nome do módulo na COR dele + o
                            total. O total existe para a seção ser legível fechada,
                            de relance, sem somar os cards com o olho. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <IconeSecao size={16} color={secao.cor} />
                            <span style={{ fontSize: '12px', fontWeight: 800, color: secao.cor, textTransform: 'uppercase', letterSpacing: '.04em' }}>{secao.titulo}</span>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff', backgroundColor: secao.cor, borderRadius: '999px', padding: '1px 8px' }}>{total}</span>
                            <div style={{ flex: 1, height: '1px', backgroundColor: COR.bordaSuave }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {itens.map((item) => (
                                <button
                                    key={item.chave}
                                    onClick={() => navigate(item.rota)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left', border: `1px solid ${item.vencido ? '#fca5a5' : COR.bordaSuave}`, borderRadius: '13px', padding: '8px 12px', backgroundColor: item.vencido ? '#fef2f2' : '#fff', cursor: 'pointer', fontFamily: FONTE }}
                                >
                                    {/* Quantidade à esquerda, no tom do módulo. Ela é o dado —
                                        o título só diz do que se trata. */}
                                    <span style={{ minWidth: '38px', height: '38px', flexShrink: 0, borderRadius: '10px', backgroundColor: `${secao.cor}1f`, color: secao.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px', padding: '0 6px' }}>
                                        {item.quantidade}
                                    </span>
                                    <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: COR.texto }}>
                                        {item.titulo}
                                        {/* ⚠️ Só a Autoavaliação tem prazo hoje (dia 1 ao 5). Quando
                                            vence, o card fica vermelho e ganha o selo — ele NÃO
                                            some, senão o atraso desapareceria justamente quando
                                            passa a importar. */}
                                        {item.vencido && (
                                            <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 800, color: '#b91c1c', backgroundColor: '#fee2e2', borderRadius: '6px', padding: '1px 6px', textTransform: 'uppercase' }}>vencido</span>
                                        )}
                                    </span>
                                    <ChevronRight size={16} color="#bbb" />
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Bloqueio({ children, rotulo = 'Em breve' }) {
    return (
        // height:100% para o card interno poder esticar até a altura da linha do grid.
        <div style={{ position: 'relative', height: '100%' }}>
            <div style={{ opacity: 0.4, pointerEvents: 'none', height: '100%' }}>{children}</div>
            <div style={{
                position: 'absolute', inset: 0, borderRadius: RAIO, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: 'rgba(242,242,242,0.35)',
            }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: COR.branco, boxShadow: SOMBRA, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COR.vermelhoEscuro }}>
                    <Lock size={22} />
                </div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#7a7a7a' }}>{rotulo}</span>
            </div>
        </div>
    );
}

// Cadeado pequeno no canto (para botões bloqueados, sem esmaecer o card inteiro).
// ⚠️ Comentário de BLOCO, não `{/* ... */}`: aqui é o escopo do MÓDULO, não JSX —
//    as chaves viravam um bloco solto (`no-lone-blocks` no lint).
/*function CadeadoCanto() {
    return (
        <div style={{ position: 'absolute', top: '8px', right: '8px', width: '26px', height: '26px', borderRadius: '50%', backgroundColor: COR.branco, boxShadow: SOMBRA, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COR.vermelhoEscuro }} title="Em breve">
            <Lock size={13} />
        </div>
    );
}
*/

export default Home;
